<template>
  <div class="pa-2 pb-10">
    <v-breadcrumbs :items="breadcrumbs" class="breadcrumbs" />
    <heading>{{ folderName }}</heading>
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-text-field
          v-model="search"
          single-line
          hide-details
          dense
          clearable
          :label="$t('general.search')"
          style="max-width: 370px"
        />
        <v-spacer />
        <v-btn
          v-if="canManageStructureHere"
          color="secondary"
          outlined
          @click="newTemplate"
        >
          {{ $t("betriebsanweisung.templateNew") }}
          <v-icon right>add</v-icon>
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="templates"
        :search="search"
        :loading="loading"
        item-key="_id"
        class="elevation-0"
        :no-data-text="$t('betriebsanweisung.noTemplates')"
        :items-per-page="-1"
        :footer-props="{ itemsPerPageOptions: [-1, 10, 25, 50] }"
      >
        <template v-slot:item.scope="{ item }">
          <v-chip
            x-small
            label
            dark
            :color="item.scope === 'global' ? 'primary' : 'secondary'"
          >
            {{
              item.scope === "global"
                ? $t("betriebsanweisung.global")
                : $t("betriebsanweisung.local")
            }}
          </v-chip>
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip small outlined :color="baTypeColor(item.type)">
            {{ $t(baTypeLabelKey(item.type)) }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn v-if="canCreateHere" icon @click="useTemplate(item)">
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditStructureHere"
            icon
            @click="openMoveDialog(item)"
            :disabled="!canEdit(item)"
          >
            <v-icon>content_paste_go</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditStructureHere"
            icon
            @click="editTemplate(item)"
            :disabled="!canEdit(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="canManageStructureHere"
            icon
            @click="confirmDelete(item)"
            :disabled="!canEdit(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <templateTargetFolderDialog
      ref="folderDialog"
      loadAction="getAllInstructionCategories"
    />
    <moveDialog
      :item="moveItem"
      :moveDialog="moveDialog"
      :loading="moving"
      :array="moveTargets"
      @cancel="moveDialog.open = false"
      @confirm="handleMoveConfirm"
    />

    <v-dialog v-model="deleteDialog.open" max-width="420">
      <v-card>
        <v-card-title class="headline">
          {{ $t("betriebsanweisung.templateDelete") }}
        </v-card-title>
        <v-card-text>{{
          deleteDialog.item && deleteDialog.item.name
        }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog.open = false">{{
            $t("general.cancel")
          }}</v-btn>
          <v-btn color="error" text @click="deleteTemplate">{{
            $t("general.delete")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { isMaster } from "../../utils/masterEmails";
import i18n from "../../i18n";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";
import templateTargetFolderDialog from "../../components/_utils/templateTargetFolderDialog.vue";
import { baTypeConfig } from "../../data/betriebsanweisungTypes";

export default {
  name: "betriebsanweisungTemplateList",
  components: { moveDialog, templateTargetFolderDialog },
  data() {
    return {
      templates: [],
      folders: [],
      loading: false,
      search: "",
      isMaster: false,
      deleteDialog: { open: false, item: null },
      moveItem: {},
      moving: false,
      moveTargets: [],
      moveDialog: {
        open: false,
        title: "betriebsanweisung.folder.moveTitle",
        description: "",
      },
    };
  },
  computed: {
    categoryId() {
      return this.$route.params.templateCategoryId;
    },
    folderName() {
      return this.$route.params.templateCategoryName || "";
    },
    breadcrumbs() {
      return [
        {
          text: i18n.t("sidebar.operatingInstructions"),
          disabled: false,
          href: "/app/instructionCategory",
        },
        {
          text: i18n.t("betriebsanweisung.templatesTitle"),
          disabled: false,
          href: "/app/betriebsanweisungTemplates",
        },
        { text: this.folderName, disabled: true },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("betriebsanweisung.title"),
          value: "name",
          align: "left",
        },
        {
          text: this.$t("betriebsanweisung.typeStatus"),
          value: "scope",
          sortable: false,
        },
        {
          text: this.$t("betriebsanweisung.documentType"),
          value: "type",
          sortable: false,
        },
        { text: "", value: "actions", align: "right", sortable: false },
      ];
    },
  },
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.isMaster = user && isMaster(user.email);
    const foldersRes = await this.$store.dispatch(
      "getBetriebsanweisungTemplateCategories"
    );
    this.folders = (foldersRes && foldersRes.data) || [];
    this.loadTemplates();
  },
  methods: {
    async useTemplate(item) {
      let target = this.$route.query.targetId
        ? { _id: this.$route.query.targetId, name: this.$route.query.targetName }
        : null;
      if (!target) target = await this.$refs.folderDialog.pick();
      if (!target) return;
      this.$router.push({
        name: "betriebsanweisungForm",
        params: {
          instructionCategoryId: target._id,
          instructionCategoryName: target.name || "-",
          id: "1",
          mode: "new",
        },
        query: { templateId: item._id },
      });
    },
    baTypeColor(type) {
      return baTypeConfig(type).color;
    },
    baTypeLabelKey(type) {
      return baTypeConfig(type).labelKey;
    },
    openMoveDialog(item) {
      this.moveItem = item;
      const targets = [
        {
          id: "none",
          name: this.$t("betriebsanweisung.folder.ohneOrdner"),
        },
      ];
      this.folders
        .filter((f) => f.type === item.scope)
        .forEach((f) => targets.push({ id: f._id, name: f.name }));
      this.moveTargets = targets.filter((t) => t.id !== this.categoryId);
      this.moveDialog.description = this.$t(
        "betriebsanweisung.folder.moveText",
        { name: item.name }
      );
      this.moveDialog.open = true;
    },
    async handleMoveConfirm(targetId) {
      try {
        this.moving = true;
        await this.$store.dispatch("updateBetriebsanweisungTemplate", {
          id: this.moveItem._id,
          category: targetId === "none" ? null : targetId,
        });
        this.moveDialog.open = false;
        this.loadTemplates();
      } finally {
        this.moving = false;
      }
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const res = await this.$store.dispatch(
          "getBetriebsanweisungTemplates",
          { category: this.categoryId }
        );
        this.templates = (res && res.data) || [];
      } finally {
        this.loading = false;
      }
    },
    canEdit(item) {
      if (this.isMaster) return true;
      return item.scope === "local";
    },
    newTemplate() {
      this.$router.push({
        name: "betriebsanweisungTemplateForm",
        params: {
          id: "1",
          mode: "new",
          templateCategoryId: this.categoryId,
          templateCategoryName: this.folderName,
        },
      });
    },
    editTemplate(item) {
      this.$router.push({
        name: "betriebsanweisungTemplateForm",
        params: { id: item._id, mode: "edit" },
      });
    },
    confirmDelete(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.open = true;
    },
    async deleteTemplate() {
      if (!this.deleteDialog.item) return;
      await this.$store.dispatch(
        "deleteBetriebsanweisungTemplate",
        this.deleteDialog.item._id
      );
      this.deleteDialog.open = false;
      this.deleteDialog.item = null;
      this.loadTemplates();
    },
  },
};
</script>
